import React from 'react';
import { Pipette } from 'lucide-react';
import { useSite } from '../../../../context/SiteContext';

interface ColorPickerFieldProps {
  label: string;
  value?: string;
  defaultValue?: string;
  onChange: (color: string) => void;
  helper?: string;
}

export const ColorPickerField: React.FC<ColorPickerFieldProps> = ({
  label,
  value,
  defaultValue = '#0F172A',
  onChange,
  helper,
}) => {
  const { activeSite } = useSite();
  const currentColor = value || defaultValue || '#0F172A';

  const themePresets = [
    { name: 'Ana Tema', color: activeSite?.settings?.primaryColor || '#0F172A' },
    { name: 'Vurgu Rengi', color: activeSite?.settings?.accentColor || '#2563EB' },
    { name: 'İkincil Renk', color: activeSite?.settings?.secondaryColor || '#64748B' },
    { name: 'Beyaz / Açık', color: '#FFFFFF' },
    { name: 'Açık Gri', color: '#F8FAFC' },
    { name: 'Koyu Slate', color: '#020617' },
    { name: 'Zümrüt', color: '#059669' },
    { name: 'Altın / Kehribar', color: '#D97706' },
  ];

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <label className="text-[11px] font-bold text-slate-300 tracking-wide uppercase">{label}</label>
        <span className="text-[10px] font-mono font-semibold text-slate-400 uppercase">{currentColor}</span>
      </div>

      {/* Main picker input */}
      <div className="flex items-center gap-2">
        <div className="relative w-10 h-10 rounded-xl overflow-hidden border border-slate-700 shadow-xs shrink-0 cursor-pointer group">
          <input
            type="color"
            value={currentColor}
            onChange={(e) => onChange(e.target.value)}
            className="absolute -inset-2 w-14 h-14 cursor-pointer opacity-0"
          />
          <div
            className="w-full h-full flex items-center justify-center transition-transform group-hover:scale-110"
            style={{ backgroundColor: currentColor }}
          >
            <Pipette className={`w-3.5 h-3.5 ${currentColor === '#FFFFFF' || currentColor.toLowerCase() === '#f8fafc' ? 'text-slate-700' : 'text-white'} opacity-60 group-hover:opacity-100 transition-opacity`} />
          </div>
        </div>

        <input
          type="text"
          value={currentColor}
          onChange={(e) => onChange(e.target.value)}
          placeholder="#000000"
          className="flex-1 bg-slate-800 border border-slate-700 text-xs font-mono font-bold text-slate-200 px-3 py-2 rounded-xl outline-none focus:border-blue-500 uppercase"
        />
      </div>

      {/* Preset Swatches */}
      <div className="flex items-center gap-1.5 pt-1 flex-wrap">
        {themePresets.map((preset, idx) => (
          <button
            key={idx}
            type="button"
            title={`${preset.name} (${preset.color})`}
            onClick={() => onChange(preset.color)}
            className={`w-5 h-5 rounded-lg border transition-transform hover:scale-125 ${
              currentColor.toLowerCase() === preset.color.toLowerCase()
                ? 'ring-2 ring-blue-500 scale-110 border-white'
                : 'border-slate-700 hover:border-slate-400'
            }`}
            style={{ backgroundColor: preset.color }}
          />
        ))}
      </div>

      {helper && <p className="text-[10px] text-slate-500">{helper}</p>}
    </div>
  );
};
